import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Video, Star, User, Stethoscope, Award, CheckCircle, Calendar, Home, ShoppingBag, Shield, Clock, Phone, Search, Filter, TrendingUp, Users, BadgeCheck } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api/axios';
import chatService from '../../services/chatService';
import Header from '../common/Header';
import Footer from '../common/Footer';
import Skeleton from 'react-loading-skeleton';

export default function OnlineDoctorsList() {
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startingChat, setStartingChat] = useState(false);

  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  useEffect(() => {
    fetchOnlineDoctors();
  }, []);

  useEffect(() => {
    filterDoctors();
  }, [doctors, searchQuery]);

  const fetchOnlineDoctors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/online-doctors/');

      if (response.data.success) {
        // Filter to only show doctors with online consultation available (chat or video)
        const onlineDoctors = response.data.data.filter(doc => 
          doc.is_available_chat || doc.is_available_video
        );
        setDoctors(onlineDoctors);
      } else {
        setError('Failed to load online doctors');
      }
    } catch (error) {
      console.error('Error fetching online doctors:', error);
      setError('Failed to load online doctors');
    } finally {
      setLoading(false);
    }
  };

  const filterDoctors = () => {
    let filtered = [...doctors];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.specialization.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredDoctors(filtered);
  };

  const handleChatClick = async (doctor) => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setStartingChat(true);
      setSelectedDoctor(doctor);

      console.log('Checking for existing active chat with doctor:', doctor.name, 'ID:', doctor.id);

      // Check if there's already an active consultation with THIS specific doctor
      // Skip cache to get fresh data
      const consultationsResponse = await api.get('/consultations/', { skipCache: true });

      console.log('All consultations:', consultationsResponse.data);

      if (consultationsResponse.data.success && consultationsResponse.data.data) {
        const allConsultations = consultationsResponse.data.data;
        console.log('Total consultations found:', allConsultations.length);

        // Find active consultation with this specific doctor
        const existingActiveChat = allConsultations.find(
          consultation => {
            console.log('Checking consultation:', {
              id: consultation.id,
              doctor_id: consultation.doctor_id,
              status: consultation.status,
              type: consultation.type,
              matches: consultation.doctor_id === doctor.id &&
                      consultation.status === 'active' &&
                      consultation.type === 'chat'
            });

            return consultation.doctor_id === doctor.id &&
                   consultation.status === 'active' &&
                   (consultation.consultation_type || consultation.type) === 'chat';
          }
        );

        if (existingActiveChat) {
          console.log('Found existing active chat:', existingActiveChat.id);
          // Navigate to existing chat with this doctor
          navigate(`/chat-consultation/${existingActiveChat.id}`);
          return;
        } else {
          console.log('No existing active chat found, creating new one');
        }
      }

      // No active chat with this doctor, create new one
      const response = await api.post('/consultation/start-chat/', {
        doctor_id: doctor.id,
        message: 'Hello Doctor, I need your help.'
      });

      if (response.data.success) {
        console.log('New chat created:', response.data.consultation_id);
        navigate(`/chat-consultation/${response.data.consultation_id}`);
      } else {
        alert('Failed to start consultation. Please try again.');
      }
    } catch (error) {
      console.error('Error starting chat consultation:', error);
      alert(error.response?.data?.error || 'Failed to start consultation. Please try again.');
    } finally {
      setStartingChat(false);
    }
  };

  const handleVideoClick = (doctor) => {
    navigate('/video-consultation', { state: { selectedDoctor: doctor } });
  };

  const specialties = ['General Physician', 'Gynecologist', 'Pediatrician', 'Dermatologist', 'Psychiatrist'];

  return (
    <>
      <Header />

      {/* Hero Section with Search */}
      <div style={{ backgroundColor: '#104a5f' }} className="text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-6">
            <h1 className="text-2xl font-semibold mb-2">Consult Top Doctors Online</h1>
            <p className="text-sm text-gray-200">
              Private consultation + Quick prescription + Follow-up care
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-2 text-gray-900">
            <div className="relative flex items-center">
              <Search className="absolute left-4 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search doctors by name or specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-12 rounded-lg text-gray-900 text-sm bg-white border-0 focus:outline-none focus:ring-2 focus:ring-white shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 w-6 h-6 flex items-center justify-center rounded-full transition-colors hover:bg-gray-200"
                  type="button"
                  style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#104a5f"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto mt-6">
            <div className="text-center">
              <p className="text-xl font-semibold">{doctors.length}+</p>
              <p className="text-xs text-gray-200">Verified Doctors</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-semibold">5K+</p>
              <p className="text-xs text-gray-200">Consultations</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-semibold">4.5★</p>
              <p className="text-xs text-gray-200">Average Rating</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">

          {/* Filters Bar */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4"> */}
              {/* Consultation Type Filters */}
              {/* <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-600">Consultation:</span>
                <button
                  onClick={() => setSelectedConsultationType('all')}
                  style={{
                    backgroundColor: selectedConsultationType === 'all' ? '#104a5f' : 'white',
                    color: selectedConsultationType === 'all' ? 'white' : '#374151',
                    borderColor: selectedConsultationType === 'all' ? '#104a5f' : '#d1d5db'
                  }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                >
                  All Doctors
                </button>
                <button
                  onClick={() => setSelectedConsultationType('chat')}
                  style={{
                    backgroundColor: selectedConsultationType === 'chat' ? '#104a5f' : 'white',
                    color: selectedConsultationType === 'chat' ? 'white' : '#374151',
                    borderColor: selectedConsultationType === 'chat' ? '#104a5f' : '#d1d5db'
                  }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1"
                >
                  <MessageCircle className="w-3 h-3" />
                  Chat
                </button>
                <button
                  onClick={() => setSelectedConsultationType('video')}
                  style={{
                    backgroundColor: selectedConsultationType === 'video' ? '#104a5f' : 'white',
                    color: selectedConsultationType === 'video' ? 'white' : '#374151',
                    borderColor: selectedConsultationType === 'video' ? '#104a5f' : '#d1d5db'
                  }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1"
                >
                  <Video className="w-3 h-3" />
                  Video
                </button>
              </div> */}

              {/* Sort Dropdown */}
              {/* <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="experience">Experience</option>
                </select>
              </div>
            </div> */}

            {/* Results count */}
            {/* <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                Showing <span className="font-semibold">{filteredDoctors.length}</span> doctors
                {searchQuery && <span> for "<span className="font-semibold">{searchQuery}</span>"</span>}
              </p>
            </div>
          </div> */}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                  <div className="flex gap-4">
                    <Skeleton circle width={80} height={80} />
                    <div className="flex-1">
                      <Skeleton height={20} width={180} className="mb-2" />
                      <Skeleton height={16} width={140} className="mb-3" />
                      <Skeleton height={14} width={200} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredDoctors.length === 0 && (
            <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
              <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Doctors Found</h3>
              <p className="text-gray-600 text-sm mb-4">
                {searchQuery ? `No results for "${searchQuery}"` : 'No doctors available at the moment'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{ backgroundColor: '#104a5f' }}
                  className="px-5 py-2 text-white rounded-lg hover:opacity-90 transition-opacity text-sm"
                >
                  Clear Search
                </button>
              )}
            </div>
          )}

          {/* Doctors List - Enhanced Card Layout */}
          {!loading && filteredDoctors.length > 0 && (
            <>
              <div className="space-y-4 mb-8">
                {filteredDoctors.map((doctor) => (
                  <div key={doctor.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200">
                    <div className="p-4">
                      <div className="flex gap-4">
                        {/* Doctor Image */}
                        <div className="flex-shrink-0">
                          <div className="relative">
                            {doctor.profile_image ? (
                              <img
                                src={`https://www.homecureexpress.com${doctor.profile_image}`}
                                alt={doctor.name}
                                className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200"
                              />
                            ) : (
                              <div style={{ backgroundColor: '#104a5f' }} className="w-20 h-20 rounded-lg flex items-center justify-center">
                                <User className="w-10 h-10 text-white" />
                              </div>
                            )}
                            {doctor.is_available_online && (
                              <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full border-2 border-white">
                                Online
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Doctor Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 text-base mb-1 flex items-center gap-2">
                                {doctor.name}
                                <BadgeCheck className="w-4 h-4 text-blue-600" />
                              </h3>
                              <p className="text-sm text-gray-600 mb-2">{doctor.specialization}</p>

                              {/* Quick Info */}
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                                {doctor.experience_years > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Award className="w-3 h-3" />
                                    {doctor.experience_years}+ years exp.
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                  4.8 (250+ reviews)
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  5K+ consultations
                                </span>
                              </div>
                            </div>

                            {/* Price */}
                            <div className="text-right ml-4">
                              <p className="text-xs text-gray-600 mb-1">Consultation Fee</p>
                              {(() => {
                                const prices = [];
                                if (doctor.is_available_chat && doctor.price_chat > 0) prices.push(parseFloat(doctor.price_chat));
                                if (doctor.is_available_video && doctor.price_video > 0) prices.push(parseFloat(doctor.price_video));
                                if (doctor.is_available_hospital_normal && doctor.price_hospital_normal > 0) prices.push(parseFloat(doctor.price_hospital_normal));
                                if (doctor.is_available_hospital_priority && doctor.price_hospital_priority > 0) prices.push(parseFloat(doctor.price_hospital_priority));
                                
                                if (prices.length === 0) {
                                  return <p className="text-sm text-gray-500">Free</p>;
                                }
                                
                                const minPrice = Math.min(...prices);
                                const maxPrice = Math.max(...prices);
                                
                                if (minPrice === maxPrice) {
                                  return <p className="text-xl font-bold" style={{ color: '#104a5f' }}>₹{minPrice.toFixed(0)}</p>;
                                } else {
                                  return (
                                    <p className="text-base font-bold" style={{ color: '#104a5f' }}>
                                      ₹{minPrice.toFixed(0)} - ₹{maxPrice.toFixed(0)}
                                    </p>
                                  );
                                }
                              })()}
                            </div>
                          </div>

                          {/* Services Badges */}
                          <div className="flex flex-wrap gap-2 mb-3">
                            {doctor.is_available_online && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                <MessageCircle className="w-3 h-3" />
                                Chat Available
                              </span>
                            )}
                            {doctor.video_call_enabled && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs">
                                <Video className="w-3 h-3" />
                                Video Available
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                              <Clock className="w-3 h-3" />
                              Responds in 5 min
                            </span>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleChatClick(doctor)}
                              disabled={startingChat && selectedDoctor?.id === doctor.id}
                              style={{ backgroundColor: '#104a5f' }}
                              className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                              {startingChat && selectedDoctor?.id === doctor.id ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>Opening Chat...</span>
                                </>
                              ) : (
                                <>
                                  <MessageCircle className="w-4 h-4" />
                                  <span>Get Doctor Advice Chat Now</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleVideoClick(doctor)}
                              style={{ backgroundColor: '#104a5f' }}
                              className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-sm font-medium border-2 border-transparent text-white hover:opacity-90 transition-opacity"
                            >
                              <Video className="w-4 h-4" />
                              <span>Book Video Consultation</span>
                            </button>
                            <button
                              onClick={() => navigate(`/doctor/${doctor.id}`)}
                              style={{ backgroundColor: '#104a5f' }}
                              className="w-full sm:w-auto px-4 py-2.5 border-2 border-transparent rounded-lg text-white hover:opacity-90 transition-opacity"
                            >
                              View Profile
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Why Choose Us Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="text-base font-semibold text-gray-900 mb-6 text-center">Why Choose Our Online Consultation</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <div style={{ backgroundColor: '#104a5f' }} className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1.5">100% Safe</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">Secure & private consultations</p>
                  </div>
                  <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <div style={{ backgroundColor: '#104a5f' }} className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                      <BadgeCheck className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1.5">Verified Doctors</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">Licensed medical professionals</p>
                  </div>
                  <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <div style={{ backgroundColor: '#104a5f' }} className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                      <Clock className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1.5">Quick Response</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">Average 5 min response time</p>
                  </div>
                  <div className="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <div style={{ backgroundColor: '#104a5f' }} className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                      <TrendingUp className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1.5">Follow-up Care</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">Continuous health support</p>
                  </div>
                </div>
              </div>

              {/* Other Services */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-1.5 text-center">Explore More Healthcare Services</h2>
                <p className="text-xs text-gray-600 text-center mb-5">Complete healthcare solutions for your family</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md cursor-pointer transition-all group"
                    style={{ borderColor: '#e5e7eb' }}
                    onClick={() => navigate('/doctor-list')}
                  >
                    <div style={{ backgroundColor: '#104a5f' }} className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">In-Person Appointment</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">Visit clinic for checkup</p>
                    </div>
                  </div>

                  <div
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md cursor-pointer transition-all group"
                    style={{ borderColor: '#e5e7eb' }}
                    onClick={() => navigate('/paramedical')}
                  >
                    <div style={{ backgroundColor: '#104a5f' }} className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Home className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Home Care Services</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">Nursing & care at home</p>
                    </div>
                  </div>

                  <div
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md cursor-pointer transition-all group"
                    style={{ borderColor: '#e5e7eb' }}
                    onClick={() => navigate('/medicines')}
                  >
                    <div style={{ backgroundColor: '#104a5f' }} className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <ShoppingBag className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Order Medicines</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">Fast delivery to doorstep</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}
